import React, { createContext, useState, useContext } from 'react';

/**
 * PropertyContext - Manages selected property for owner users
 *
 * This context allows property owners to switch between their properties
 * to view property-specific data and metrics.
 *
 * Pattern: Mirrors AccountContext for consistency (Salesforce/Stripe model)
 */

const PropertyContext = createContext({
  selectedProperty: null,
  setSelectedProperty: () => {},
  clearProperty: () => {}
});

const STORAGE_KEY = 'hostme_selected_property';

export const PropertyProvider = ({ children }) => {
  // Initialize from sessionStorage
  const [selectedProperty, setSelectedPropertyState] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);

      // Validate: Only accept MongoDB ObjectId format (24 hex characters)
      // This prevents legacy sentinel values ('portfolio', 'all', etc.) and invalid data
      if (stored && /^[0-9a-fA-F]{24}$/.test(stored)) {
        return stored;
      }

      // Invalid or legacy format - clear storage and return null
      if (stored) {
        sessionStorage.removeItem(STORAGE_KEY);
      }

      return null;
    } catch (error) {
      // SessionStorage not available (e.g., incognito mode)
      console.warn('SessionStorage not available:', error);
      return null;
    }
  });

  // Persist to sessionStorage on change
  const setSelectedProperty = (propertyId) => {
    // Validate: Only store valid ObjectId or null
    if (propertyId && !/^[0-9a-fA-F]{24}$/.test(propertyId)) {
      console.warn('🏢 Invalid property ID format, not storing:', propertyId);
      setSelectedPropertyState(null);
      return;
    }

    setSelectedPropertyState(propertyId);

    try {
      if (propertyId) {
        sessionStorage.setItem(STORAGE_KEY, propertyId);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Failed to persist to sessionStorage:', error);
    }
  };

  // Clear property selection (show all)
  const clearProperty = () => {
    setSelectedProperty(null);
  };

  const value = {
    selectedProperty,
    setSelectedProperty,
    clearProperty
  };

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
};

// Custom hook for using property context
export const useProperty = () => {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error('useProperty must be used within PropertyProvider');
  }
  return context;
};

export default PropertyContext;
